import { useEffect, useState } from 'react';
import { ApiError, fetchFleet, type FleetFilters } from './api';
import type { FleetHub, FleetPage } from './types';

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 90) return `${seconds}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

function releaseLabel(hub: FleetHub): string {
  const selected = hub.selectedRelease;
  const pin = hub.pinnedRelease;
  if (pin) return `${pin.version} (pinned)`;
  if (selected) return selected.version;
  // A hub can carry a pin id that no release answers to — the resolver refuses
  // to serve one, so surfacing the absence is the point.
  if (hub.pinnedRelease === null && hub.selectedRelease === null) return '—';
  return '—';
}

export function Fleet({ onOpen }: { onOpen: (hubId: string) => void }) {
  const [filters, setFilters] = useState<FleetFilters>({ limit: 50, offset: 0 });
  const [page, setPage] = useState<FleetPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);

    fetchFleet(filters)
      .then((result) => { if (live) { setPage(result); setError(null); } })
      .catch((caught) => { if (live) setError(caught instanceof ApiError ? caught.message : String(caught)); })
      .finally(() => { if (live) setLoading(false); });

    return () => { live = false; };
  }, [filters]);

  const patch = (change: Partial<FleetFilters>) =>
    // Any filter change resets to the first page: keeping the offset would
    // land the operator on an empty page of a shorter result set.
    setFilters((current) => ({ ...current, ...change, offset: 0 }));

  return (
    <>
      <div className="controls">
        <input
          placeholder="hub id contains…"
          onChange={(event) => patch({ q: event.target.value || undefined })}
        />
        <select onChange={(event) => patch({ status: event.target.value || undefined })}>
          <option value="">any status</option>
          <option value="online">online</option>
          <option value="offline">offline</option>
          <option value="updating">updating</option>
        </select>
        <select onChange={(event) => patch({ channel: event.target.value || undefined })}>
          <option value="">any channel</option>
          <option value="stable">stable</option>
          <option value="beta">beta</option>
        </select>
        <label className="muted">
          <input
            type="checkbox"
            onChange={(event) => patch({ decommissioned: event.target.checked || undefined })}
          />{' '}
          include decommissioned
        </label>
        <span className="spacer" />
        {page && (
          <span className="muted">
            {page.total === 0 ? 'no hubs' : `${page.offset + 1}–${Math.min(page.offset + page.limit, page.total)} of ${page.total}`}
          </span>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">loading…</p>}

      <table>
        <thead>
          <tr>
            <th>hub</th>
            <th>home</th>
            <th>status</th>
            <th>last seen</th>
            <th>firmware</th>
            <th>channel</th>
            <th>release</th>
            <th>disk</th>
            <th>tunnel</th>
            <th>last update</th>
          </tr>
        </thead>
        <tbody>
          {page?.hubs.map((hub) => (
            <tr key={hub.hubId} onClick={() => onOpen(hub.hubId)} style={{ cursor: 'pointer' }}>
              <td className={hub.decommissionedAt ? 'retired' : ''}>{hub.hubId}</td>
              <td className="muted">{hub.homeName ?? 'unclaimed'}</td>
              <td className={`status-${hub.status}`}>
                {hub.state === 'claimed' ? hub.status : hub.state}
              </td>
              <td>{ago(hub.lastSeen)}</td>
              <td>{hub.firmwareVersion}</td>
              <td>
                {hub.effectiveChannel}
                {hub.updateChannel === null && <span className="muted"> (default)</span>}
              </td>
              <td>{releaseLabel(hub)}</td>
              <td>
                {hub.systemHealth ? `${Math.round(hub.systemHealth.diskUsedPercent)}%` : '—'}
              </td>
              <td className="muted">{hub.tunnelProvisioned ? 'provisioned' : '—'}</td>
              <td className="muted">{hub.lastUpdate ? hub.lastUpdate.status : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="controls" style={{ marginTop: 12 }}>
        <button
          disabled={(page?.offset ?? 0) === 0}
          onClick={() => setFilters((current) => ({
            ...current,
            offset: Math.max((current.offset ?? 0) - (current.limit ?? 50), 0),
          }))}
        >
          previous
        </button>
        <button
          disabled={!page || page.offset + page.limit >= page.total}
          onClick={() => setFilters((current) => ({
            ...current,
            offset: (current.offset ?? 0) + (current.limit ?? 50),
          }))}
        >
          next
        </button>
      </div>
    </>
  );
}
